<template>
  <div>
    <div
      :class="[
        'tabs__header',
        pills ? 'tabs__header--pills' : 'tabs__header--desktop'
      ]"
      role="tablist"
      aria-label="Tabs"
    >
      <button
        v-for="(tab, index) in tabs"
        :id="slugify(tab.props?.title)"
        :key="tab.key as number"
        :uuid="index"
        role="tab"
        :aria-selected="index == selectedIndex"
        aria-controls="tabpanel-id"
        :class="[
          { tab__selected: index == selectedIndex },
          { 'tab--pills paragraph-m': pills },
          { 'heading--5': !pills },
          'tab',
        ]"
        @click="(e) => selectTab(e, index)"
      >
        {{ tab.props?.title }}
      </button>
    </div>
    <slot />
  </div>
</template>
<script lang="ts" setup>
import { slugify } from '@/utils/helpers'

const slots = useSlots()
const emit = defineEmits({
  tabChanged(index: number) {
    return index !== undefined || index !== null
  },
})

interface Props {
  pills?: boolean
}

defineProps<Props>()

const tabs = computed(() => {
  return slots.default?.()[0]?.children as VNode[]
})

const selectedIndex = ref(0)

provide('selectedIndex', selectedIndex)

const selectTab = (_event: Event, index: number) => {
  selectedIndex.value = index
  if (Array.isArray(tabs.value)) {
    tabs.value?.forEach((tab: any, index) => {
      tab.isActive = index === selectedIndex.value
    })
  }
  emit('tabChanged', index)
}
</script>

<style lang="scss">
.tabs__header {
  display: flex;
  list-style: none;
  margin-bottom: var(--space56);

  &--mobile {
    display: flex;
    flex-direction: column;
    @media (min-width: $breakpoint-lg) {
      display: none;
    }
  }

  &--desktop {
    display: none;
    @media (min-width: $breakpoint-lg) {
      display: flex;
    }
  }

  &--pills {
    display: flex !important;
    gap: var(--space16);
    flex-direction: row;
    flex-wrap: wrap;
  }
}

.tab {
  padding: 0 var(--space32) var(--space24) var(--space24);
  border-bottom: 2px solid var(--gray-400);
  display: inline-block;
  text-align: left;

  &--pills {
    @include shadow-grey-small;

    padding: var(--space12) var(--space24);
    border-radius: 30px;
    border: none !important;
    background-color: var(--white);
    font-weight: var(--font-weight-semibold); 
    transition: background-color 0.2s ease-in-out;

    &:hover {
      background-color: var(--cobalt-200);
    }
  }
}

.tab:first-of-type:not(.tab--pills) {
  padding-left: 0;
}

.tab__selected {
  color: var(--cobalt-600);
  border-bottom: 2px solid var(--cobalt-600);

  &.tab--pills {
    background-color: var(--cobalt-200);
    color: var(--color-text);
  }
}
</style>
